import {ReactElement, useEffect, useMemo, useState} from "react";
import {Col, Grid, Row} from "@zendeskgarden/react-grid";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row as TableRow, Table} from "@zendeskgarden/react-tables";
import {ReactComponent as PencilIcon} from '@zendeskgarden/svg-icons/src/16/pencil-stroke.svg';
import {Checkbox, Field, Input, Label} from "@zendeskgarden/react-forms";
import {TagSelector} from "../coa-tags/components/TagSelector";
import {useProperties} from "../../contexts/properties/PropertiesContext";
import {Button} from "@zendeskgarden/react-buttons";
import * as css from "./styles/propertyTags.module.scss";
import {PropertyWithTags} from "./logic/types";
import {
    useAddPropertyTagsMutation,
    useQueryPropertyTagsLazyQuery,
    useRemovePropertyTagsMutation,
    useSetPropertyTagsMutation
} from "../../__generated__/generated_types";
import {Inline} from "@zendeskgarden/react-loaders";
import {toast} from "react-toastify";
import {Tag} from "@zendeskgarden/react-tags";

export function PropertyTags(): ReactElement {
    const {properties: propertiesRaw} = useProperties();
    const [propertiesWithTags, setPropertiesWithTags] = useState<PropertyWithTags[]>([]);
    const [filteredProperties, setFilteredProperties] = useState<PropertyWithTags[]>([]);
    const [propertyIdForTagsUpdate, setPropertyIdForTagsUpdate] = useState<string>();
    const [showSelectTagsForFilter, setShowSelectTagsForFilter] = useState<boolean>(false);
    const [propertyNameFilter, setPropertyNameFilter] = useState<string>("");
    const [tagsFilter, setTagsFilter] = useState<string[]>([]);
    const [allTags, setAllTags] = useState<string[]>([]);
    const [selectionTags, setSelectionTags] = useState<string[]>([]);
    const [showBulkAddVisible, setShowBulkAddTagsSelected] = useState<boolean>(false);
    const [showBulkRemoveVisible, setShowBulkRemoveTagsFromSelected] = useState<boolean>(false);

    const [queryPropertyTags, {data: propertyTagsData, loading: propertyTagsLoading}] = useQueryPropertyTagsLazyQuery({fetchPolicy: "no-cache"});

    const [setPropertyTags] = useSetPropertyTagsMutation({
        onCompleted: (d) => {
            if(d?.setPropertyTags === true) {
                toast.success("Tags Updated");
            }
            else {
                toast.error("Failed To Update Tags");
            }
            triggerReloadTags();
        }
    });
    const [addPropertyTags] = useAddPropertyTagsMutation({
        onCompleted: (d) => {
            if(d?.addPropertyTags === true) {
                toast.success("Tags Updated");
            }
            else {
                toast.error("Failed To Update Tags");
            }
            triggerReloadTags();
        }
    });
    const [removePropertyTags] = useRemovePropertyTagsMutation({
        onCompleted: (d) => {
            if(d?.removePropertyTags === true) {
                toast.success("Tags Updated");
            }
            else {
                toast.error("Failed To Update Tags");
            }
            triggerReloadTags();
        }
    });

    function triggerReloadTags() {
        const ids = propertiesRaw?.map(p => p.id) ?? [];
        if(ids.length > 0) {
            queryPropertyTags({variables: {propertyIds: ids}});
        }
    }

    function updateFilteredProperties(properties: PropertyWithTags[]) {
        setFilteredProperties(properties);
        // remove selection from filtered out properties
        for(const property of propertiesWithTags) {
            if(!properties.find(p => p.id == property.id)) {
                property.selected = false;
            }
        }
    }

    useEffect(() => {
        if(propertiesRaw) {
            const rows = propertiesRaw.map(property => ({...property, tags: [], selected: false, tagsLoading: true}));
            setPropertiesWithTags(rows);
            updateFilteredProperties(rows);
            const ids = propertiesRaw.map(p => p.id);
            if(ids.length > 0) {
                queryPropertyTags({variables: {propertyIds: ids}});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertiesRaw]);

    useEffect(() => {
        const nameFiltered = propertyNameFilter && propertyNameFilter.length > 0
            ? propertiesWithTags.filter(property => property.name.toLowerCase().includes(propertyNameFilter.toLowerCase()))
            : propertiesWithTags;
        const tagFiltered = tagsFilter.length > 0
            ? nameFiltered.filter(p => p.tags.length > 0 && p.tags.some(t => tagsFilter.includes(t)))
            : nameFiltered;
            updateFilteredProperties(tagFiltered);
    }, [propertyNameFilter, tagsFilter, propertiesWithTags]);

    useEffect(() => {
        if(propertyTagsData && !propertyTagsLoading) {
            const tagsById = new Map(propertyTagsData.queryPropertyTags.map(pt => [pt.propertyId, pt.tags] as const));
            const updated = propertiesWithTags.map(p => ({
                ...p,
                tagsLoading: false,
                tags: (tagsById.get(p.id) ?? []).sort()
            }));
            const all = new Set<string>();
            const sel = new Set<string>();
            for(const p of updated) {
                for(const t of p.tags) {
                    all.add(t);
                    if(p.selected) sel.add(t);
                }
            }
            setAllTags(Array.from(all));
            setTagsFilter(tagsFilter.filter(t => all.has(t)));
            setSelectionTags(Array.from(sel));
            setPropertiesWithTags(updated);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyTagsData, propertyTagsLoading]);

    const visibleSelectedCount = useMemo(() => filteredProperties.filter(p => p.selected).length, [filteredProperties]);

    function handleSelect(property: PropertyWithTags, selected: boolean) {
        const updated = [...propertiesWithTags];
        const sel = new Set<string>();
        for(const p of updated) {
            if(p.id === property.id) {
                p.selected = selected;
            }
            if(p.selected) {
                p.tags.forEach(t => sel.add(t));
            }
        }
        setPropertiesWithTags(updated);
        setSelectionTags(Array.from(sel));
    }

    function selectAll(selected: boolean) {
        const idsVisible = new Set(filteredProperties.map(p => p.id));
        const updated = propertiesWithTags.map(p => idsVisible.has(p.id) ? {...p, selected} : p);
        setPropertiesWithTags(updated);
        const sel = new Set<string>();
        for(const p of updated) if(p.selected) p.tags.forEach(t => sel.add(t));
        setSelectionTags(Array.from(sel));
    }

    function handleUpdateSingle(propertyId: string, tags: string[]) {
        setPropertiesWithTags(prev => prev.map(p => p.id === propertyId ? {...p, tagsLoading: true} : p));
        setPropertyTags({variables: {propertyIds: [propertyId], tags: tags.map(t => t.trim())}});
    }

    function handleBulkAdd(tags: string[]) {
        const ids = propertiesWithTags.filter(p => p.selected).map(p => p.id);
        if(ids.length === 0) {
            return;
        }
        setPropertiesWithTags(prev => prev.map(p => ids.includes(p.id) ? {...p, tagsLoading: true} : p));
        addPropertyTags({variables: {propertyIds: ids, tags: tags.map(t => t.trim())}});
    }

    function handleBulkRemove(tags: string[]) {
        const ids = propertiesWithTags.filter(p => p.selected).map(p => p.id);
        if(ids.length === 0 || tags.length === 0) {
            return;
        }
        setPropertiesWithTags(prev => prev.map(p => ids.includes(p.id) ? {...p, tagsLoading: true} : p));
        removePropertyTags({variables: {propertyIds: ids, tags}});
    }

    return (
        <>
            <Grid>
                <Row>
                    <Col>
                        <div className={css.headerActions}>
                            <Field>
                                <Input
                                    type="text"
                                    placeholder="Filter By Property Name"
                                    value={propertyNameFilter}
                                    onChange={e => setPropertyNameFilter(e.target.value)}
                                    aria-label="Filter by property name"
                                />
                            </Field>
                            <div className={css.rightActions}>
                                <Field className={css.filterField}>
                                    <Input
                                        type="text"
                                        placeholder="Filter By Tags"
                                        className={css.filterInput}
                                        value={tagsFilter.join(", ")}
                                        readOnly
                                        onClick={() => setShowSelectTagsForFilter(true)}
                                        aria-label="Filter by tags"
                                    />
                                </Field>
                                <Button isPrimary disabled={visibleSelectedCount === 0} onClick={() => setShowBulkAddTagsSelected(true)}>Add Tags</Button>
                                <Button isDanger disabled={visibleSelectedCount === 0 || selectionTags.length === 0} onClick={() => setShowBulkRemoveTagsFromSelected(true)}>Remove Tags</Button>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Grid>
            <Grid>
                <Row>
                    <Col>
                        <Table>
                            <Head>
                                <HeaderRow>
                                    <HeaderCell isMinimum className={css.checkBoxCell}>
                                        <Field className={css.checkboxField}>
                                            <Checkbox
                                                checked={visibleSelectedCount >= 0 && visibleSelectedCount === filteredProperties.length}
                                                indeterminate={visibleSelectedCount !== 0 && visibleSelectedCount !== filteredProperties.length}
                                                onChange={() => {
                                                    if(visibleSelectedCount >= 0 && visibleSelectedCount !== filteredProperties.length) {
                                                        selectAll(true);
                                                    }
                                                    else {
                                                        selectAll(false);
                                                    }
                                                }}>
                                                <Label />
                                            </Checkbox>
                                        </Field>
                                    </HeaderCell>
                                    <HeaderCell className={css.nameCell}>Property Name</HeaderCell>
                                    <HeaderCell className={css.nameCell}>Tags</HeaderCell>
                                </HeaderRow>
                            </Head>
                            <Body>
                                {filteredProperties.length == 0 ? (
                                    <TableRow>
                                        <Cell colSpan={3} style={{textAlign: "center"}}>No properties found</Cell>
                                    </TableRow>
                                ) : (
                                    filteredProperties.map(property => (
                                        <TableRow key={property.id}>
                                            <Cell className={css.checkBoxCell}>
                                                <Field className={css.checkboxField}>
                                                    <Checkbox checked={property.selected} onChange={() => handleSelect(property, !property.selected)}>
                                                        <Label />
                                                    </Checkbox>
                                                </Field>
                                            </Cell>
                                            <Cell className={css.nameCell}>{property.name}</Cell>
                                            <Cell className={css.assignTagsCell}>
                                                <div className={css.tagsContainer}>
                                                    {property.tagsLoading ?
                                                        <Inline aria-label="loading" /> :
                                                        property.tags.map(t => <Tag className={css.tag} key={t}>{t}</Tag>)}
                                                </div>
                                                <Button
                                                    onClick={() => setPropertyIdForTagsUpdate(property.id)}
                                                    aria-label="Update tags"
                                                    size="small"
                                                    className={css.updateTagsButton}
                                                >
                                                    <PencilIcon />
                                                </Button>
                                            </Cell>
                                        </TableRow>
                                    )))}
                            </Body>
                        </Table>
                    </Col>
                </Row>
            </Grid>
            {propertyIdForTagsUpdate && (() => {
                const target = propertiesWithTags.find(p => p.id === propertyIdForTagsUpdate);
                const selected = (target?.tags ?? []).map(t => ({label: t, value: t}));
                return (
                    <TagSelector
                        title={`Edit tags for property: ${propertiesRaw?.find(property => property.id === propertyIdForTagsUpdate)?.name ?? ""}`}
                        options={allTags.map(t => ({label: t, value: t}))}
                        selected={selected}
                        canAdd={true}
                        onConfirm={(tags) => {handleUpdateSingle(propertyIdForTagsUpdate, tags.map(t => t.value)); setPropertyIdForTagsUpdate(undefined);}}
                        onClose={() => setPropertyIdForTagsUpdate(undefined)}
                    />
                );
            })()}
            {showSelectTagsForFilter && (
                <TagSelector
                    title="Filter By Tags"
                    options={allTags.map(t => ({label: t, value: t}))}
                    selected={tagsFilter.map(t => ({label: t, value: t}))}
                    canAdd={false}
                    onConfirm={(tags) => {setTagsFilter(tags.map(t => t.value)); setShowSelectTagsForFilter(false);}}
                    onClose={() => setShowSelectTagsForFilter(false)}
                />
            )}
            {showBulkAddVisible && (
                <TagSelector
                    title="Bulk Add Tags to Selected Properties"
                    options={allTags.map(t => ({label: t, value: t}))}
                    selected={[]}
                    canAdd={true}
                    onConfirm={(tags) => {handleBulkAdd(tags.map(t => t.value)); setShowBulkAddTagsSelected(false);}}
                    onClose={() => setShowBulkAddTagsSelected(false)}
                />
            )}
            {showBulkRemoveVisible && (
                <TagSelector
                    title="Bulk Remove Tags From Selected Properties"
                    options={selectionTags.map(t => ({label: t, value: t}))}
                    selected={[]}
                    canAdd={false}
                    onConfirm={(tags) => {handleBulkRemove(tags.map(t => t.value)); setShowBulkRemoveTagsFromSelected(false);}}
                    onClose={() => setShowBulkRemoveTagsFromSelected(false)}
                />
            )}
        </>
    );
}
